import { Box, Button, Stack } from '@chakra-ui/react';
import { TextStyleKit } from '@tiptap/extension-text-style';
import type { Editor } from '@tiptap/react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useEffect } from 'react';

const extensions = [TextStyleKit, StarterKit];

function MenuBar({ editor }: { editor: Editor | null }) {
  if (!editor) {
    return null;
  }
  // Read the current editor's state, and re-render the component when it changes
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isBold: ctx.editor.isActive('bold') ?? false,
        canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
        isItalic: ctx.editor.isActive('italic') ?? false,
        canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
        isStrike: ctx.editor.isActive('strike') ?? false,
        canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
        isCode: ctx.editor.isActive('code') ?? false,
        canCode: ctx.editor.can().chain().toggleCode().run() ?? false,
        canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
        isParagraph: ctx.editor.isActive('paragraph') ?? false,
        isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
        isHeading4: ctx.editor.isActive('heading', { level: 4 }) ?? false,
        isHeading5: ctx.editor.isActive('heading', { level: 5 }) ?? false,
        isHeading6: ctx.editor.isActive('heading', { level: 6 }) ?? false,
        isBulletList: ctx.editor.isActive('bulletList') ?? false,
        isOrderedList: ctx.editor.isActive('orderedList') ?? false,
        isCodeBlock: ctx.editor.isActive('codeBlock') ?? false,
        isBlockquote: ctx.editor.isActive('blockquote') ?? false,
        canUndo: ctx.editor.can().chain().undo().run() ?? false,
        canRedo: ctx.editor.can().chain().redo().run() ?? false,
      };
    },
  });

  return (
    <div className="control-group">
      <Stack className="button-group" direction="row" spacing={1} mb={1}>
        <Button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editorState.canBold}
          variant="outline"
          colorScheme={editorState.isBold ? 'primary' : 'gray'}
          isActive={editorState.isBold}
          size="sm"
        >
          Bold
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editorState.canItalic}
          variant="outline"
          colorScheme={editorState.isItalic ? 'primary' : 'gray'}
          isActive={editorState.isItalic}
          size="sm"
        >
          Italic
        </Button>
        <Button
          onClick={() => editor.chain().focus().setParagraph().run()}
          variant="outline"
          colorScheme={editorState.isParagraph ? 'primary' : 'gray'}
          isActive={editorState.isParagraph}
          size="sm"
        >
          Paragraph
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          variant="outline"
          colorScheme={editorState.isHeading1 ? 'primary' : 'gray'}
          isActive={editorState.isHeading1}
          size="sm"
        >
          H1
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          variant="outline"
          colorScheme={editorState.isHeading2 ? 'primary' : 'gray'}
          isActive={editorState.isHeading2}
          size="sm"
        >
          H2
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          variant="outline"
          colorScheme={editorState.isHeading3 ? 'primary' : 'gray'}
          isActive={editorState.isHeading3}
          size="sm"
        >
          H3
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          variant="outline"
          colorScheme={editorState.isBulletList ? 'primary' : 'gray'}
          isActive={editorState.isBulletList}
          size="sm"
        >
          Bullet list
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          variant="outline"
          colorScheme={editorState.isOrderedList ? 'primary' : 'gray'}
          isActive={editorState.isOrderedList}
          size="sm"
        >
          Ordered list
        </Button>
        <Button
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editorState.canUndo}
          variant="outline"
          colorScheme={editorState.canUndo ? 'primary' : 'gray'}
          isActive={editorState.canUndo}
          size="sm"
        >
          Undo
        </Button>
        <Button
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editorState.canRedo}
          variant="outline"
          colorScheme={editorState.canRedo ? 'primary' : 'gray'}
          isActive={editorState.canRedo}
          size="sm"
        >
          Redo
        </Button>
      </Stack>
    </div>
  );
}

export default function RichTextEditor({
  value,
  onChange,
}: {
  value?: string | null;
  onChange: (html: string) => void;
}) {
  const editor = useEditor({
    extensions,
    content: value ?? '',
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'tiptap',
      },
    },
    onUpdate: ({ editor }) => {
      try {
        onChange(editor.getHTML());
      } catch {}
    },
  });
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value ?? '';
    if (current !== next) {
      editor.commands.setContent(next);
    }
  }, [value, editor]);
  return (
    <div>
      <MenuBar editor={editor} />
      <Box
        sx={{
          '.tiptap': {
            minHeight: '200px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '8px',
            borderWidth: '1px',
            borderRadius: '6px',
            borderColor: 'gray.200',
            '&:focus': { outline: 'none', borderColor: 'primary.500' },
            '& p': { margin: 0, marginBottom: '8px', lineHeight: 1 },
            '& h1': { fontSize: '2xl', fontWeight: 'bold', marginTop: '4px', marginBottom: '4px' },
            '& h2': { fontSize: 'xl', fontWeight: 'semibold', marginTop: '4px', marginBottom: '4px' },
            '& h3': { fontSize: 'lg', fontWeight: 'semibold', marginTop: '4px', marginBottom: '4px' },
            '& ul, & ol': { paddingLeft: '20px', marginTop: '6px', marginBottom: '6px' },
            '& ul': { listStyleType: 'disc' },
            '& ol': { listStyleType: 'decimal' },
            '& li': { marginTop: '4px', marginBottom: '4px' },
            '& *:first-child': { marginTop: 0 },
            '& *:last-child': { marginBottom: 0 },
            '& strong': { color: 'inherit', fontWeight: 'bold' },
          },
          '.tiptap .is-empty::before': {
            content: 'attr(data-placeholder)',
            color: 'gray.400',
            float: 'left',
            height: 0,
            pointerEvents: 'none',
          },
        }}
      >
        <EditorContent id="rich-text-editor" editor={editor} />
      </Box>
    </div>
  );
}
